import { ChecklistItem } from '../core-data-model/Common.model';
import { Name } from './Name';

export interface EngageReport {
    title: string;
    endDate: Date;
    responseRate: number;
    responseCount: number;
    employeeCount: number;
    dimensionScores: EngageDimScore[];
    impacts: EngageImpactScore[];
    name: string;
    isOrgReport: boolean;
    departmentName: string;
}

export interface EngageScore {
    name: string | EngageDimension;
    score: number;
    benchmark: number;
    impact?: number;
}

export type EngageSurveyResponseInfoSVG = {
    totalSurveyTakers: number;
    completedSurveys: number;
};

export interface EngageDimScore extends EngageScore {
    questions: EngageScore[];
}

export interface EngageImpactScore extends EngageScore {
    isBlindSpot: boolean;
    description: string;
    actions: EngageAction[];
}

export interface EngageAction {
    challenge: string;
    action: string;
    tool: string;
}

export enum EngageScoreRenderType {
    Small = 1,
    Medium,
    Large,
    Legend,
    ProgressBar,
    ScoreBar
}

export type EngageRunSurveyDisplayProperties = {
    isBackHidden: boolean;
    isNextDisabled: boolean;
    isBackDisabled: boolean;
    currentStep: number;
};

export type EngageRunSurveySetup = {
    title: string;
    employeeCount: number;
    schedule: EngageRunSurveySchedule;
};

export type EngageRunSurveySchedule = {
    startDate: Date;
    endDate: Date;
    inviteEmail: EngageRunSurveyEmailTemplate;
    reminderEmails: EngageRunSurveyEmailTemplate[];
    closeEmail: EngageRunSurveyEmailTemplate;
};

export type EngageRunSurveyEmailTemplate = {
    name?: string;
    date: Date;
    subject?: string;
    template?: string;
};

export enum ExternalEngageSurveyProvider {
    Qualtrics = 0,
    SurveyMonkey = 1
}

export type EngageSurveyInfo = {
    id: string;
    title: string;
    isActive: boolean;
    schedule: EngageSurveySchedule;
    employeeCount: number;
    responseCount: number;
    checklistItems: EngageSurveyCheckListDictionary;
    excludedFromSurveyCount: number;
    externalSurveyProvider: ExternalEngageSurveyProvider;
};

export type EngageSurveyCheckListDictionary = {
    [key: string]: ChecklistItem[];
};

export type EngageSurveyUpdate = {
    engageSurveyId: string;
    checklistItems: EngageSurveyCheckListDictionary;
};

export type EngageSurveySchedule = {
    startDate: string;
    endDate: string;
    inviteEmail: EngageSurveyEmail;
    closeEmail: EngageSurveyEmail;
    reminderEmails: EngageSurveyEmail[];
};

export type EngageSurveyEmail = {
    name: string;
    date: string;
};

export type EngageScoreRenderTypeMap = {
    [key: number]: EngageRenderType;
};

export type EngageRenderType = {
    graphStartOffset: number;
    scoreWidth: number;
    markerWidth: number;
    scoreBottomMarker: number;
    benchmarkBottomMarker: number;
};

export enum EngageChecklistCategory {
    unknown,
    preSurvey,
    duringSurvey,
    postSurvey
}

// Survey Insights
export enum EngageDimension {
    Engagement = 'Engagement',
    Job = 'Job',
    Manager = 'Manager',
    People = 'People',
    Organization = 'Organization'
}

export interface DimensionScoreInfo extends EngageScore {
    questions: EngageScore[];
}

export type SurveyInsights = {
    id: string;
    dimensionScores: DimensionScoreInfo[];
    impacts: EngageImpactScore[];
    heatMapItems: HeatMapItem[];
};

export type HeatMapItem = {
    isOrgData: boolean;
    category: string;
    name: Name;
    responseCount: number;
    dimensionScores: DimensionScoreInfo[];
};

export enum InsightItems {
    OrganizationResults,
    HeatMap
}
